import { useCallback, useEffect, useRef } from 'react'

import { useLogger } from '../useLogger/useLogger'

import { config } from '@percent/workplace-giving/config/config'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'

const OUTLOOK_OAUTH_STATE_KEY = 'outlook-oauth-state'

type OAuthParams = {
  code: string | null
  state: string | null
  error: string | null
  errorDescription: string | null
}

let cachedOAuthParams: OAuthParams | null = null

export const resetOAuthParamsCache = () => {
  cachedOAuthParams = null
}

const getOAuthParams = (): OAuthParams => {
  if (!cachedOAuthParams) {
    const params = new URLSearchParams(window.location.search)
    cachedOAuthParams = {
      code: params.get('code'),
      state: params.get('state'),
      error: params.get('error'),
      errorDescription: params.get('error_description')
    }
  }

  return cachedOAuthParams
}

type UseOutlookCalendarAuthProps = {
  onSuccess: (code: string) => void
  onError: (error: Error) => void
  enabled?: boolean
}

export const useOutlookCalendarAuth = ({ onSuccess, onError, enabled = true }: UseOutlookCalendarAuthProps) => {
  const { logError } = useLogger()
  const handledRef = useRef(false)

  useEffect(() => {
    if (!enabled || handledRef.current) return

    const { code, state, error, errorDescription } = getOAuthParams()
    const savedState = sessionStorage.getItem(OUTLOOK_OAUTH_STATE_KEY)

    if (!savedState || state !== savedState) return

    handledRef.current = true
    sessionStorage.removeItem(OUTLOOK_OAUTH_STATE_KEY)
    window.history.replaceState({}, '', window.location.pathname)

    if (error) {
      const authError = new Error(errorDescription || error)
      logError(authError)
      onError(authError)

      return
    }

    if (code) {
      onSuccess(code)
    }
  }, [enabled, logError, onError, onSuccess])

  const connectCalendar = useCallback(() => {
    const clientId = config.keys.azureClientId
    const tenantId = config.keys.azureTenantId
    const redirectUri = `${window.location.origin}${AppRoute.SETTINGS}`

    if (!clientId || !tenantId) {
      const error = new Error('Azure OAuth is not configured')
      logError(error)
      onError(error)

      return
    }

    const stateValue = crypto.randomUUID()
    sessionStorage.setItem(OUTLOOK_OAUTH_STATE_KEY, stateValue)

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      redirect_uri: redirectUri,
      scope: 'https://graph.microsoft.com/Calendars.ReadWrite offline_access',
      prompt: 'select_account',
      state: stateValue
    })

    const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params}`
    window.location.assign(authUrl)
  }, [logError, onError])

  return connectCalendar
}
